import { PARAMS } from '../../../constants/pages';
import { primaryPalette } from '../../../styles/theme';
import {
  prepareDivisionsWithDepartments,
  prepareTracksWithLevels,
  prepareAllTracksLevels,
} from '../../../utility/uiUtils';
import { ReactComponent as TrackIcon } from '../../../assets/icons/track-icon.svg';
import { ReactComponent as DivisionIcon } from '../../../assets/icons/account_tree.svg';
import { ReactComponent as DepartmentIcon } from '../../../assets/icons/apartment.svg';
import { ReactComponent as LevelIcon } from '../../../assets/icons/track-level-icon.svg';
import { ReactComponent as JobTitleIcon } from '../../../assets/icons/job-title-icon.svg';

export const prepareOptions = yMaxValue => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: primaryPalette.bluish1,
        font: {
          size: 14,
          family: 'ProximaNova-Regular',
        },
      },
    },
    y: {
      max: yMaxValue,
      beginAtZero: true,
      ticks: {
        display: false,
      },
      border: {
        display: true,
        color: primaryPalette.bluish4,
      },
      grid: {
        color: context => {
          const value = context?.tick?.value;
          if (!value) return 'transparent';
          return primaryPalette.bluish7;
        },
        drawTicks: false,
        borderDash: [3, 3],
      },
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
        pointStyle: 'rectRounded',
        color: primaryPalette.bluish1,
        boxWidth: 10,
        boxHeight: 10,
        padding: 30,
        font: {
          size: 14,
          family: 'ProximaNova-Regular',
        },
        filter: legendItem => legendItem.datasetIndex !== 0,
      },
    },
    tooltip: {
      callbacks: {
        label: item => `${item.dataset.label}: ${item.raw}`,
      },
    },
  },
});

export const buildGenderChartData = (translations, data) => ({
  labels: data.map(item => item.label),
  datasets: [
    {
      label: translations.headcount,
      data: data.map(item => item.total),
      dates: data.map(item => item.date),
      backgroundColor: primaryPalette.bluish9,
      borderColor: primaryPalette.bluish4,
      borderWidth: 1,
      grouped: false,
      barThickness: 60,
      borderSkipped: false,
      order: 99,
    },
    {
      label: translations.male,
      data: data.map(item => item.male),
      backgroundColor: primaryPalette.blue4,
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 14,
      order: 1,
    },
    {
      label: translations.female,
      data: data.map(item => item.female),
      backgroundColor: primaryPalette.pink1,
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 14,
      order: 1,
    },
    {
      label: translations.other,
      data: data.map(item => item.other),
      backgroundColor: primaryPalette.yellow1,
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 14,
      order: 1,
    },
    {
      label: translations.noData,
      data: data.map(item => item.no_data),
      backgroundColor: primaryPalette.bluish7,
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 14,
      order: 1,
    },
  ],
});

export const buildAgeChartData = (translations, data) => ({
  labels: data.map(item => item.label),
  datasets: [
    {
      label: translations.headcount,
      data: data.map(item => item.total),
      dates: data.map(item => item.date),
      backgroundColor: primaryPalette.bluish9,
      borderColor: primaryPalette.bluish4,
      borderWidth: 1,
      grouped: false,
      barThickness: 60,
      borderSkipped: false,
      order: 99,
    },
    {
      label: translations.ageGroupOne,
      data: data.map(item => item[translations.ageGroupOne]),
      backgroundColor: primaryPalette.green2,
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 9,
      order: 1,
    },
    {
      label: translations.ageGroupTwo,
      data: data.map(item => item[translations.ageGroupTwo]),
      backgroundColor: primaryPalette.green1,
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 9,
      order: 1,
    },
    {
      label: translations.ageGroupThree,
      data: data.map(item => item[translations.ageGroupThree]),
      backgroundColor: '#F2994A',
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 9,
      order: 1,
    },
    {
      label: translations.ageGroupFour,
      data: data.map(item => item[translations.ageGroupFour]),
      backgroundColor: '#E68733',
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 9,
      order: 1,
    },
    {
      label: translations.ageGroupFive,
      data: data.map(item => item[translations.ageGroupFive]),
      backgroundColor: '#C06615',
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 9,
      order: 1,
    },
    {
      label: translations.noData,
      data: data.map(item => item.no_data),
      backgroundColor: primaryPalette.bluish7,
      borderRadius: 0,
      borderSkipped: false,
      barThickness: 9,
      order: 1,
    },
  ],
});

export const headcountDecorations = {
  id: 'headcountDecorations',
  afterDatasetsDraw(chart) {
    const {
      ctx,
      data,
      chartArea: { top },
      scales: { x },
    } = chart;
    const totalDataset = data.datasets[0];
    const totalMeta = chart.getDatasetMeta(0);
    if (!totalDataset || !totalMeta?.data?.length) {
      return;
    }

    ctx.save();
    ctx.fillStyle = primaryPalette.bluish4;
    ctx.font = '400 14px ProximaNova-Regular';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    totalMeta.data.forEach((bar, index) => {
      ctx.fillText(`${totalDataset.data[index]}`, bar.x, bar.y - 6);
    });

    const years = (totalDataset.dates || []).map(
      date => String(date).split('-')[0]
    );
    const yearDividerIndex = years.findIndex(
      (year, index) => index > 0 && year !== years[index - 1]
    );

    if (yearDividerIndex > 0) {
      const dividerX =
        x.getPixelForValue(yearDividerIndex - 1) +
        x.getPixelForValue(yearDividerIndex);
      const yearDividerX = dividerX / 2;
      ctx.strokeStyle = primaryPalette.bluish4;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.fillText(years[yearDividerIndex], yearDividerX + 28, top + 18);
      ctx.moveTo(yearDividerX, top - 10);
      ctx.lineTo(yearDividerX, chart.scales.y.getPixelForValue(0));
      ctx.stroke();
    }

    ctx.fillStyle = primaryPalette.bluish4;
    ctx.font = '400 14px ProximaNova-Regular';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    if (years[0]) {
      ctx.fillText(years[0], x.getPixelForValue(0) - 28, top + 10);
    }
    ctx.restore();
  },
};

export const prepareLevelDonutChartData = (data, selectedTrack) => {
  if (!data) return null;

  return Object.values(data).reduce((acc, curr) => {
    const level = selectedTrack?.levels?.find(l => l.id === curr.id);

    if (level) {
      return [
        ...acc,
        {
          name: level.name,
          value: curr.total_users,
        },
      ];
    }

    return acc;
  }, []);
};

export const prepareDepartmentDonutChartData = (data, selectedDivision) => {
  if (!data) return null;

  return Object.values(data).reduce((acc, curr) => {
    const department = selectedDivision?.departments?.find(
      d => d.id === curr.id
    );

    if (department) {
      return [
        ...acc,
        {
          name: department.name,
          value: curr.total_users,
        },
      ];
    }

    return acc;
  }, []);
};

export const preparePageFilters = (
  translations,
  jobTitles,
  tracks,
  divisions,
  departments
) => {
  const {
    JOB_TITLE: JOB_TITLE_FILTER,
    TRACK: TRACK_FILTER,
    LEVEL: LEVEL_TRACK_FILTER,
    DIVISION: DIVISION_FILTER,
    DEPARTMENT: DEPARTMENT_FILTER,
  } = PARAMS;

  return [
    {
      id: JOB_TITLE_FILTER,
      name: translations.filters.labels[JOB_TITLE_FILTER],
      icon: JobTitleIcon,
      items: jobTitles,
    },
    {
      id: TRACK_FILTER,
      name: translations.filters.labels.track,
      icon: TrackIcon,
      items: prepareTracksWithLevels(tracks),
    },
    {
      id: LEVEL_TRACK_FILTER,
      name: translations.filters.labels.trackLevel,
      icon: LevelIcon,
      items: prepareAllTracksLevels(tracks),
    },
    {
      id: DIVISION_FILTER,
      name: translations.filters.labels.division,
      icon: DivisionIcon,
      items: prepareDivisionsWithDepartments(divisions),
    },
    {
      id: DEPARTMENT_FILTER,
      name: translations.filters.labels.department,
      icon: DepartmentIcon,
      items: departments,
    },
  ];
};
